import { Injectable, signal } from '@angular/core';
import { BridgeMessages, BridgeResult, CaseModel, InitialFlipType } from '@ortho-next/nextray-core';
import { from, Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import type { BaseEvent, EventListener, Vector2Like } from 'three';
import { AppModel } from '../../nextray/Core/AppModel';
import { Bridge, BridgeActions } from '../../nextray/Core/Bridge';
import { Main } from '../../nextray/Core/Main';
import { ImageOrientationEnum } from '../core';
import { ImageItem } from '../models';

type BridgeEvents = BridgeResult<BridgeMessages>;

/**
 * This service handles three.js canvas data.
 */
@Injectable({ providedIn: 'root' })
export class CanvasService {

	private _model?: AppModel;
	private _bridge?: Bridge;
	private _main?: Main;

	private _initialized = false;
	private _categorizationCanvas?: HTMLDivElement;

	leftLabel = signal<string>('');
	rightLabel = signal<string>('');
	topLabel = signal<string>('');
	bottomLabel = signal<string>('');

	/**
	 * Initialize main canvas container.
	 */
	init(): void {
		if (this._initialized) {
			this.restoreCategorizationCanvas();
		} else {
			this._categorizationCanvas = document.getElementById('categorization-canvas') as HTMLDivElement;
			this._main = new Main(this._categorizationCanvas);
			this._model = this._main?.model;
			this._bridge = this._main?.bridge;
			this._initialized = true;
		}
	}

	private restoreCategorizationCanvas(): void {
		document.getElementById('categorization-canvas')?.remove();
		this._categorizationCanvas && document.getElementById('categorization-canvas-container')?.appendChild(this._categorizationCanvas);
		this._main?.updateCanvasMeasures();
	}

	/**
	 * Set canvas image in base 64 format.
	 */
	setImage(file: File): void {
		this.getBase64Image(file).pipe(
			map(img => { return { imgAP: img, brightnessAP: 0, contrastAP: 0 } as CaseModel }),
			switchMap(caseModel => from(this._main!.loadScenes(caseModel))),
			first()
		).subscribe();
	}

	/**
	 * Set canvas model to edit.
	 */
	setModel(imageData: ImageItem): void {
		of(this.caseModelMapper(imageData)).pipe(
			switchMap(caseModel => from(this._main!.loadScenes(caseModel))),
			first()
		).subscribe();
	}
	/**
	 * Get scale factor.
	 */
	get scaleFactor(): number | undefined {
		return this._model?.scaleFactorAP;
	}
	/**
	 * Get brightness of image.
	 */
	public get brightness(): number | undefined {
		return this._model?.brightnessAP;
	}
	/**
	 * Get contrast of image.
	 */
	public get contrast(): number | undefined {
		return this._model?.contrastAP;
	}
	/**
	 * Check if registering mode is active.
	 */
	get isPointsRegistering(): boolean {
		return !!this._model?.stateDescription?.isPointsPositioningRunning;
	}
	/**
	 * Get running workflow.
	 */
	get runningWorkflow(): number | undefined {
		return this._model?.stateDescription?.isWorkflowRunning;
	}
	/**
	 * Get scale factor.
	 */
	get calibrationEllipsePoints(): Vector2Like[] | undefined {
		return this._model?.calibrationEllipsePoints?.map(v => ({ x: v.x, y: v.y }));
	}
	/**
	 * Get calibration ring matrix.
	 */
	get ringMatrix(): number[] | undefined {
		return this._model?.ringMatrix?.toArray();
	}
	/**
	 * Get calibration ring angle.
	 */
	get angle(): number | undefined {
		return this._model?.angle;
	}
	/**
	 * Get calibration ring frontal rotation.
	 */
	get frontal(): number | undefined {
		return this._model?.frontal;
	}
	/**
	* Get calibration ring axial rotation.
	*/
	get axial(): number | undefined {
		return this._model?.axial;
	}
	/**
	* Get plane rotation.
	*/
	get planeRotation(): number | undefined {
		return this._model?.planeRotation;
	}

	/**
	 * Dispatch an event or action.
	 */
	dispatch<K extends keyof BridgeActions>(type: K, args?: BridgeActions[K]): void {
		this._bridge?.mapEvent(type, args!);
	}

	/**
	* Add event listener on bridge.
	*/
	addEventListener<T extends keyof BridgeEvents>(type: T, listener: EventListener<BridgeEvents[T], T, Bridge>): void {
		this._bridge?.addEventListener(type, listener);
	}

	/**
	* Check if has an event listener on bridge.
	*/
	hasEventListener<T extends keyof BridgeEvents>(type: T, listener: EventListener<BridgeEvents[T], T, Bridge>): boolean {
		return !!this._bridge?.hasEventListener(type, listener);
	}

	/**
	* Remove an event listener on bridge.
	*/
	removeEventListener<T extends keyof BridgeEvents>(type: T, listener: EventListener<BridgeEvents[T], T, Bridge>): void {
		this._bridge?.removeEventListener(type, listener);
	}

	/**
	* Dispatch an event listener on bridge.
	*/
	dispatchEvent<T extends keyof BridgeEvents>(event: BaseEvent<T> & BridgeEvents[T]): void {
		this._bridge?.dispatchEvent(event);
	}

	private getBase64Image(file: File): Observable<string> {
		return new Observable<string>(obs => {
			const reader = new FileReader();
			reader.onerror = err => obs.error(err);
			reader.onabort = err => obs.error(err);
			reader.onload = () => obs.next(reader.result as string);
			reader.onloadend = () => obs.complete();
			reader.readAsDataURL(file);
			return () => reader.abort();
		});
	}

	private caseModelMapper(imageData: ImageItem): CaseModel {
		const model = {} as CaseModel;
		model.imgAP = imageData.url ?? undefined;
		model.scaleFactorAP = imageData.scaleFactor ?? undefined;

		switch (imageData.orientation) {
			case ImageOrientationEnum.Horizontal: model.flipTypeAP = InitialFlipType.horizontal; break;
			case ImageOrientationEnum.Vertical: model.flipTypeAP = InitialFlipType.vertical; break;
			case ImageOrientationEnum.Rotate180: model.flipTypeAP = InitialFlipType.rotate180; break;
			default: model.flipTypeAP = undefined;
		}

		model.brightnessAP = imageData.brightness ?? 0;
		model.contrastAP = imageData.contrast ?? 0;
		model.planeRotationAP = imageData.planeRotation;

		return model;
	}

}

export enum BoneImageEnum {
	Left_Leg_AP = 'images/bones/bones_left_orientation_AP.png',
	Left_Leg_LT = 'images/bones/bones_left_orientation_LT.png',
	Right_Leg_AP = 'images/bones/bones_right_orientation_AP.png',
	Right_Leg_LT = 'images/bones/bones_right_orientation_LT.png',

	Left_Foot_AP = 'images/bones/feet_left_orientation_AP.png',
	Left_Foot_LT = 'images/bones/feet_left_orientation_LT.png',
	Right_Foot_AP = 'images/bones/feet_right_orientation_AP.png',
	Right_Foot_LT = 'images/bones/feet_right_orientation_LT.png',
}